/**
 * AnalyticsDashboardService - Service for fetching analytics dashboard data
 *
 * Handles communication with the backend analytics API for dashboard data,
 * including date filtering, KPIs, tool usage stats, and funnel data.
 */

export interface DashboardKPIs {
  activeUsers: {
    value: number
    trend: number
  }
  completionRate: {
    value: number
    trend: number
  }
  avgSessionDuration: {
    value: number
    trend: number
  }
  systemMetrics?: {
    uniqueSessions: {value: number; trend: number}
    uniqueUsers: {value: number; trend: number}
    totalErrors: {value: number; trend: number}
  }
  topTool?: {
    name: string
    percentage: number
  }
}

export interface ToolUsageStat {
  toolName: string
  sessionCount: number
  percentage: number
  eventCount: number
  avgDuration: number
}

export interface FunnelStep {
  step: string
  users: number
  percentage: number
  conversionRate: number
}

export interface DashboardData {
  success: boolean
  dateRange: {
    startDate: string
    endDate: string
    previousStartDate: string
    previousEndDate: string
  }
  kpis: DashboardKPIs
  toolUsage: ToolUsageStat[]
  funnelData: FunnelStep[]
  generatedAt: string
}

export interface DateRange {
  startDate: Date
  endDate: Date
}

export class AnalyticsDashboardService {
  private baseUrl: string

  constructor() {
    // Determine base URL based on environment
    this.baseUrl = this.getBaseUrl()
  }

  private apiFetch(url: string, init?: RequestInit): Promise<Response> {
    return fetch(url, { credentials: 'include', ...init })
  }

  /**
   * Get base URL for API requests
   */
  private getBaseUrl(): string {
    // Import config dynamically to avoid issues with SSR
    try {
      const { config } = require('../config/environment')
      return `${config.API_BASE_URL}/api`
    } catch (error) {
      // Fallback for development
      return 'http://localhost:3000/api'
    }
  }

  /**
   * Fetch dashboard data with optional date range and tool filter
   */
  async getDashboardData(
    startDate?: Date,
    endDate?: Date,
    toolName?: string,
    department?: string
  ): Promise<DashboardData> {
    try {
      let url = `${this.baseUrl}/analytics/dashboard-data`

      // Add date parameters and tool filter if provided
      const params = new URLSearchParams()
      if (startDate && endDate) {
        params.append('startDate', startDate.toISOString())
        params.append('endDate', endDate.toISOString())
      }
      if (toolName) {
        params.append('toolName', toolName)
      }
      if (department) {
        params.append('department', department)
      }

      if (params.toString()) {
        url += `?${params.toString()}`
      }

      const response = await this.apiFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        )
      }

      const data: DashboardData = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      throw error
    }
  }


  /**
   * Fetch top departments leaderboard
   */
  async getTopDepartments(startDate?: Date, endDate?: Date): Promise<any[]> {
    try {
      let url = `${this.baseUrl}/analytics/top-departments`

      if (startDate && endDate) {
        const params = new URLSearchParams({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        })
        url += `?${params.toString()}`
      }

      const response = await this.apiFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        )
      }

      const data = await response.json()
      // Handle both old and new response formats
      return data.data?.departments || data.data || []
    } catch (error) {
      console.error('Error fetching top departments:', error)
      throw error
    }
  }

  async getTopDepartmentsPaginated(
    startDate?: Date,
    endDate?: Date,
    page: number = 1,
    limit: number = 25,
    toolName?: string
  ): Promise<{
    departments: any[]
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      })

      if (startDate && endDate) {
        params.append('startDate', startDate.toISOString())
        params.append('endDate', endDate.toISOString())
      }
      if (toolName) {
        params.append('toolName', toolName)
      }

      const url = `${this.baseUrl}/analytics/top-departments?${params.toString()}`

      const response = await this.apiFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        )
      }

      const data = await response.json()
      return data.data || { departments: [], pagination: { page: 1, limit: 25, total: 0, totalPages: 0 } }
    } catch (error) {
      console.error('Error fetching paginated top departments:', error)
      throw error
    }
  }

  /**
   * Fetch top users leaderboard
   */
  async getTopUsers(startDate?: Date, endDate?: Date): Promise<any[]> {
    try {
      let url = `${this.baseUrl}/analytics/top-users`

      if (startDate && endDate) {
        const params = new URLSearchParams({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        })
        url += `?${params.toString()}`
      }

      const response = await this.apiFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        )
      }

      const data = await response.json()
      // Handle both old and new response formats
      return data.data?.users || data.data || []
    } catch (error) {
      console.error('Error fetching top users:', error)
      throw error
    }
  }

  async getTopUsersPaginated(
    startDate?: Date,
    endDate?: Date,
    page: number = 1,
    limit: number = 25,
    department?: string,
    toolName?: string
  ): Promise<{
    users: any[]
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      })

      if (startDate && endDate) {
        params.append('startDate', startDate.toISOString())
        params.append('endDate', endDate.toISOString())
      }
      if (department) {
        params.append('department', department)
      }
      if (toolName) {
        params.append('toolName', toolName)
      }

      const url = `${this.baseUrl}/analytics/top-users?${params.toString()}`

      const response = await this.apiFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        )
      }

      const data = await response.json()
      return data.data || { users: [], pagination: { page: 1, limit: 25, total: 0, totalPages: 0 } }
    } catch (error) {
      console.error('Error fetching paginated top users:', error)
      throw error
    }
  }

  /**
   * Fetch top tools leaderboard
   */
  async getTopTools(startDate?: Date, endDate?: Date): Promise<any[]> {
    try {
      let url = `${this.baseUrl}/analytics/top-tools`

      if (startDate && endDate) {
        const params = new URLSearchParams({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        })
        url += `?${params.toString()}`
      }

      const response = await this.apiFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        )
      }

      const data = await response.json()
      // Handle both old and new response formats
      return data.data?.tools || data.data || []
    } catch (error) {
      console.error('Error fetching top tools:', error)
      throw error
    }
  }

  async getTopToolsPaginated(
    startDate?: Date,
    endDate?: Date,
    page: number = 1,
    limit: number = 25,
    department?: string
  ): Promise<{
    tools: any[]
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      })

      if (startDate && endDate) {
        params.append('startDate', startDate.toISOString())
        params.append('endDate', endDate.toISOString())
      }
      if (department) {
        params.append('department', department)
      }

      const url = `${this.baseUrl}/analytics/top-tools?${params.toString()}`

      const response = await this.apiFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        )
      }

      const data = await response.json()
      return data.data || { tools: [], pagination: { page: 1, limit: 25, total: 0, totalPages: 0 } }
    } catch (error) {
      console.error('Error fetching paginated top tools:', error)
      throw error
    }
  }

  /**
   * Get available tools for filtering
   */
  async getAvailableTools(): Promise<{ tools: string[] }> {
    try {
      const url = `${this.baseUrl}/analytics/available-tools`

      const response = await this.apiFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        )
      }

      const data = await response.json()
      return data.data || { tools: [] }
    } catch (error) {
      console.error('Error fetching available tools:', error)
      throw error
    }
  }

  /**
   * Fetch tools by department matrix data
   */
  async getToolsByDepartment(startDate?: Date, endDate?: Date): Promise<any> {
    try {
      let url = `${this.baseUrl}/analytics/tools-by-department`

      if (startDate && endDate) {
        const params = new URLSearchParams({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        })
        url += `?${params.toString()}`
      }

      const response = await this.apiFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        )
      }

      const data = await response.json()
      return data.data || {departments: [], tools: [], data: []}
    } catch (error) {
      console.error('Error fetching tools by department:', error)
      throw error
    }
  }

  /**
   * Health check for analytics service
   */
  async healthCheck(): Promise<{
    status: string
    timestamp: string
    stats?: any
  }> {
    try {
      const response = await this.apiFetch(`${this.baseUrl}/analytics/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error checking analytics health:', error)
      throw error
    }
  }

  /**
   * Get default date range (current month)
   */
  getDefaultDateRange(): DateRange {
    const now = new Date()
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1)
    const endDate = new Date(now.getTime()) // Use current time exactly

    return {startDate, endDate}
  }

  /**
   * Get preset date ranges for quick selection
   */
  getPresetDateRanges(): Array<{label: string; range: DateRange}> {
    const now = new Date()

    return [
      {
        label: 'Current Month',
        range: {
          startDate: new Date(now.getFullYear(), now.getMonth(), 1),
          endDate: new Date(now.getTime()) // Use current time exactly
        }
      },
      {
        label: 'Previous Month',
        range: {
          startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1),
          endDate: new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59)
        }
      },
      {
        label: 'Last 3 Months',
        range: {
          startDate: new Date(now.getFullYear(), now.getMonth() - 2, 1),
          endDate: new Date(now.getTime()) // Use current time exactly
        }
      },
      {
        label: 'Last 6 Months',
        range: {
          startDate: new Date(now.getFullYear(), now.getMonth() - 5, 1),
          endDate: new Date(now.getTime()) // Use current time exactly
        }
      },
      {
        label: 'Year to Date',
        range: {
          startDate: new Date(now.getFullYear(), 0, 1),
          endDate: new Date(now.getTime()) // Use current time exactly
        }
      }
    ]
  }

  /**
   * Validate date range (max 2 years)
   */
  validateDateRange(
    startDate: Date,
    endDate: Date
  ): {valid: boolean; error?: string} {
    const now = new Date()
    const twoYearsAgo = new Date()
    twoYearsAgo.setFullYear(now.getFullYear() - 2)
    const maxRange = 2 * 365 * 24 * 60 * 60 * 1000 // 2 years in milliseconds

    if (startDate > endDate) {
      return {valid: false, error: 'Start date must be before end date'}
    }

    if (endDate > now) {
      return {valid: false, error: 'End date cannot be in the future'}
    }

    if (startDate < twoYearsAgo) {
      return {
        valid: false,
        error: 'Date range cannot exceed 2 years from current date'
      }
    }

    if (endDate.getTime() - startDate.getTime() > maxRange) {
      return {valid: false, error: 'Date range cannot exceed 2 years'}
    }

    return {valid: true}
  }
}

// Create singleton instance
export const analyticsDashboardService = new AnalyticsDashboardService()
